/**
 * EditSectionsWalkthrough.jsx
 *
 * User request (2026-02-28):
 *   "When I edit, I would like it to have a prompt on each section
 *    showing me first what accounts are in each section and asking me
 *    do I want to keep or replace, then allow me to say which account
 *    I'm keeping in that section and I can remove accounts if they
 *    need to be removed or I can add accounts to that section if they
 *    need to be added, then it moves on section by section. It will
 *    also allow me to change the type of account that's going to be
 *    placed in that section."
 *
 * Behaviour: Walks Sections 1→5 like a wizard.  Each section shows:
 *   • Section type dropdown (11 fraud types)
 *   • Every current account with Keep / Remove / Replace buttons
 *   • "Add another account" button that opens a picker of the
 *     client's other accounts (not already in ANY section)
 *   • Next → / Previous ← navigation, "Save & Close" on Section 5
 *
 * On Replace: opens the same picker, then asks the user each time
 * (per user preference 4c) whether to copy the removed account's
 * fraud amount and dates onto the newly-picked account.
 *
 * On save the parent's `savedReportSections` is updated via
 * onSave(nextSections) — parent then treats this as an "edit-as-is"
 * proceed and drops into the builder with the modified data.
 */

import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import {
  Check, X, RefreshCw, Plus, ChevronLeft, ChevronRight, Search,
  CreditCard, Banknote, Car, Smartphone, Zap, Home,
  Stethoscope, GraduationCap, Building2, Umbrella, HelpCircle,
  Save,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Same 11 fraud types + icons that FTCReportBuilderNew.jsx uses so
// the section-type picker matches the rest of the app.
const FRAUD_TYPES = [
  { value: "CREDIT_CARD", label: "Credit card",                Icon: CreditCard,   color: "text-blue-300" },
  { value: "LOAN_LEASE",  label: "Personal / Business loan",   Icon: Banknote,     color: "text-purple-300" },
  { value: "AUTO_LOAN",   label: "Auto loan / Lease",          Icon: Car,          color: "text-red-300" },
  { value: "PHONE",       label: "Mobile phone",               Icon: Smartphone,   color: "text-amber-300" },
  { value: "UTILITY",     label: "Utility",                    Icon: Zap,          color: "text-teal-300" },
  { value: "RENTAL",      label: "Rental (Apartment/House)",   Icon: Home,         color: "text-orange-300" },
  { value: "MEDICAL",     label: "Medical services",           Icon: Stethoscope,  color: "text-pink-300" },
  { value: "STUDENT_LOAN",label: "Private student loan",       Icon: GraduationCap,color: "text-indigo-300" },
  { value: "REAL_ESTATE", label: "Real estate loan",           Icon: Building2,    color: "text-emerald-300" },
  { value: "INSURANCE",   label: "Insurance",                  Icon: Umbrella,     color: "text-cyan-300" },
  { value: "OTHER",       label: "Other",                      Icon: HelpCircle,   color: "text-zinc-300" },
];

function labelForType(t) {
  const found = FRAUD_TYPES.find(f => f.value === t);
  return found ? found.label : (t || 'Uncategorized');
}
function IconForType(t) {
  const found = FRAUD_TYPES.find(f => f.value === t);
  return found ? found.Icon : HelpCircle;
}

export default function EditSectionsWalkthrough({
  open,
  onOpenChange,
  clientId,
  sections,       // {1:{fraudType, accounts:[]}, 2:{...}, …}
  onSave,         // (nextSections) => void
}) {
  const [step, setStep] = useState(1);
  const [localSections, setLocalSections] = useState({});
  const [clientAccounts, setClientAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [pickerFor, setPickerFor] = useState(null);  // { mode:'add'|'replace', replaceAccountId? }
  const [pickerSearch, setPickerSearch] = useState('');
  const [pendingCopy, setPendingCopy] = useState(null); // {oldAccount, newAccount}

  // ─── Hydrate localSections from parent whenever the modal opens ──
  useEffect(() => {
    if (!open) return;
    const next = {};
    for (const n of [1, 2, 3, 4, 5]) {
      const src = sections?.[n] || sections?.[String(n)] || {};
      next[n] = {
        fraudType: src.fraudType || null,
        customTotalAmount: src.customTotalAmount || '',
        accounts: Array.isArray(src.accounts)
          ? src.accounts.map(a => ({ ...a }))
          : [],
      };
    }
    setLocalSections(next);
    setStep(1);
    setPickerFor(null);
    setPickerSearch('');
    setPendingCopy(null);
  }, [open, sections]);

  // ─── Fetch the client's accounts once so we can offer them in the
  // Add / Replace pickers. ─────────────────────────────────────────
  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    (async () => {
      setLoadingAccounts(true);
      try {
        const r = await axios.get(`${API}/api/accounts?client_id=${clientId}&dedupe=false`);
        if (!cancelled) setClientAccounts(r.data || []);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load accounts');
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, clientId]);

  const currentSection = localSections[step] || { fraudType: null, accounts: [] };

  // Accounts NOT already anywhere in the report — what the picker
  // should show.  Filtered by the picker's search string.
  const usedAccountIds = useMemo(() => {
    const set = new Set();
    for (const n of [1, 2, 3, 4, 5]) {
      (localSections[n]?.accounts || []).forEach(a => a?.id && set.add(a.id));
    }
    return set;
  }, [localSections]);

  const pickerCandidates = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    return (clientAccounts || [])
      .filter(a => !usedAccountIds.has(a.id))
      .filter(a => {
        if (!q) return true;
        const hay = `${a.display_name || a.creditor_name} ${a.category || ''} ${a.account_number || ''}`.toLowerCase();
        return hay.includes(q);
      });
  }, [clientAccounts, usedAccountIds, pickerSearch]);

  // ─── Mutation helpers ────────────────────────────────────────────
  const mutateSection = (mut) => {
    setLocalSections(prev => ({
      ...prev,
      [step]: mut(prev[step] || { fraudType: null, accounts: [] }),
    }));
  };

  const removeAccountAt = (idx) => {
    mutateSection(s => ({
      ...s,
      accounts: s.accounts.filter((_, i) => i !== idx),
    }));
  };

  const openReplaceFor = (idx) => {
    setPickerSearch('');
    setPickerFor({ mode: 'replace', replaceIdx: idx });
  };

  const openAdd = () => {
    setPickerSearch('');
    setPickerFor({ mode: 'add' });
  };

  const applyPickerPick = (picked) => {
    if (!pickerFor) return;
    const newAccountRow = {
      id: picked.id,
      creditor_name: picked.display_name || picked.creditor_name,
      display_name: picked.display_name || picked.creditor_name,
      original_name: picked.original_name || picked.creditor_name,
      account_number: picked.account_number || '',
      date_opened: picked.date_opened || '',
      original_date_opened: picked.original_date_opened || picked.date_opened || '',
      balance: picked.balance || 0,
      category: picked.category || null,
    };
    if (pickerFor.mode === 'add') {
      mutateSection(s => ({ ...s, accounts: [...s.accounts, newAccountRow] }));
      setPickerFor(null);
      toast.success(`${newAccountRow.creditor_name} added to Section ${step}`);
      return;
    }
    // Replace flow — ask about copying old data first (per user 4c)
    const oldAcc = currentSection.accounts[pickerFor.replaceIdx];
    setPendingCopy({
      oldAccount: oldAcc,
      newAccountRow,
      replaceIdx: pickerFor.replaceIdx,
    });
    setPickerFor(null);
  };

  const finishReplace = (copyOldValues) => {
    if (!pendingCopy) return;
    const { oldAccount, newAccountRow, replaceIdx } = pendingCopy;
    const merged = copyOldValues
      ? {
          ...newAccountRow,
          balance: oldAccount?.balance ?? newAccountRow.balance,
          date_opened: oldAccount?.date_opened ?? newAccountRow.date_opened,
          original_date_opened: oldAccount?.original_date_opened ?? newAccountRow.original_date_opened,
        }
      : newAccountRow;
    mutateSection(s => ({
      ...s,
      accounts: s.accounts.map((a, i) => i === replaceIdx ? merged : a),
    }));
    setPendingCopy(null);
    toast.success(`Section ${step} updated — ${merged.creditor_name} replaces ${oldAccount?.creditor_name || 'previous account'}`);
  };

  // ─── Navigation ──────────────────────────────────────────────────
  const goPrev = () => setStep(s => Math.max(1, s - 1));
  const goNext = () => setStep(s => Math.min(5, s + 1));
  const isLast = step === 5;

  const handleSaveAll = () => {
    onSave(localSections);
    onOpenChange(false);
    toast.success('Report changes saved — opening in the wizard');
  };

  // ─── Render ──────────────────────────────────────────────────────
  const SectionIcon = IconForType(currentSection.fraudType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-zinc-950 text-white border-zinc-700 max-h-[92vh] overflow-y-auto"
        data-testid="edit-sections-walkthrough"
      >
        {/* Header */}
        <div className="border-b border-zinc-800 pb-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-zinc-500">Editing</span>
              <span className="text-base font-bold text-white">Section {step} of 5</span>
              <SectionIcon className="w-5 h-5 text-blue-300" />
            </div>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(n => {
                const count = localSections[n]?.accounts?.length || 0;
                const isCur = n === step;
                return (
                  <button
                    key={n}
                    onClick={() => setStep(n)}
                    className={`w-8 h-8 rounded-full text-xs font-bold transition ${
                      isCur
                        ? 'bg-blue-500 text-white ring-2 ring-blue-300'
                        : count > 0
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30'
                          : 'bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700'
                    }`}
                    data-testid={`walkthrough-jump-${n}`}
                    title={count > 0 ? `${count} account${count === 1 ? '' : 's'}` : 'empty'}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </div>
          <p className="text-[12px] text-zinc-400">
            Keep, Remove, or Replace each account below. Add more accounts, or change this section&apos;s account type. Section {step} currently holds {currentSection.accounts.length} account{currentSection.accounts.length === 1 ? '' : 's'}.
          </p>
        </div>

        {/* Fraud type picker */}
        <div className="mb-4">
          <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">
            Section {step} type — change if you&apos;re swapping the account category
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
            {FRAUD_TYPES.map(t => {
              const Icon = t.Icon;
              const isSel = currentSection.fraudType === t.value;
              return (
                <button
                  key={t.value}
                  onClick={() => mutateSection(s => ({ ...s, fraudType: t.value }))}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md border text-[11px] transition ${
                    isSel
                      ? 'border-blue-400 bg-blue-500/20 text-white'
                      : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800'
                  }`}
                  data-testid={`walkthrough-type-${t.value}`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isSel ? 'text-blue-300' : t.color}`} />
                  <span className="truncate">{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Account list */}
        <div className="space-y-2 mb-3">
          {currentSection.accounts.length === 0 && (
            <div className="border border-dashed border-zinc-700 rounded-lg p-4 text-center text-zinc-500 text-sm">
              No accounts in this section yet.<br />
              Click <span className="text-blue-300 font-semibold">Add Account</span> below to fill it, or move on to the next section.
            </div>
          )}
          {currentSection.accounts.map((a, idx) => (
            <div
              key={`${a.id || 'na'}-${idx}`}
              className="flex items-start justify-between gap-2 bg-zinc-900 border border-zinc-800 rounded-lg p-3"
              data-testid={`walkthrough-account-${idx}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white truncate">
                  {a.display_name || a.creditor_name || '(unnamed)'}
                </div>
                <div className="text-[11px] text-zinc-400 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  {a.account_number && <span>#{a.account_number}</span>}
                  {a.date_opened && <span>Opened {a.date_opened}</span>}
                  {a.balance ? <span className="text-emerald-300">${Number(a.balance).toLocaleString()}</span> : null}
                  {a.category && <span className="text-zinc-500">{a.category}</span>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-1 flex-shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                  title="Keep this account"
                  data-testid={`walkthrough-keep-${idx}`}
                >
                  <Check className="w-3.5 h-3.5 mr-1" />
                  Keep
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  onClick={() => removeAccountAt(idx)}
                  data-testid={`walkthrough-remove-${idx}`}
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  Remove
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                  onClick={() => openReplaceFor(idx)}
                  data-testid={`walkthrough-replace-${idx}`}
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />
                  Replace
                </Button>
              </div>
            </div>
          ))}

          {/* Add account button */}
          <Button
            variant="outline"
            className="w-full border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
            onClick={openAdd}
            data-testid="walkthrough-add-account"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add another account to Section {step}
          </Button>
        </div>

        {/* Footer navigation */}
        <div className="border-t border-zinc-800 pt-3 flex items-center justify-between gap-2">
          <Button
            variant="outline"
            onClick={goPrev}
            disabled={step === 1}
            className="border-zinc-700"
            data-testid="walkthrough-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Section {step - 1}
          </Button>
          <div className="text-[11px] text-zinc-500">
            Section {step} of 5
          </div>
          {isLast ? (
            <Button
              onClick={handleSaveAll}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="walkthrough-save"
            >
              <Save className="w-4 h-4 mr-1" />
              Save & Open Wizard
            </Button>
          ) : (
            <Button
              onClick={goNext}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="walkthrough-next"
            >
              Section {step + 1}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        {/* ── Nested: Account picker for Add / Replace ────────────── */}
        {pickerFor && (
          <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4">
            <div className="w-full max-w-xl bg-zinc-950 border border-zinc-700 rounded-xl p-4 max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <div className="text-white font-bold">
                  {pickerFor.mode === 'add' ? 'Pick an account to ADD' : 'Pick an account to REPLACE with'}
                </div>
                <button
                  onClick={() => setPickerFor(null)}
                  className="text-zinc-400 hover:text-white"
                  data-testid="walkthrough-picker-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="relative mb-2">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Search by creditor name, category, account #…"
                  className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-700 rounded-md text-sm text-white"
                  data-testid="walkthrough-picker-search"
                  autoFocus
                />
              </div>
              <div className="overflow-y-auto flex-1 space-y-1">
                {loadingAccounts && <div className="text-zinc-500 text-sm py-4 text-center">Loading accounts…</div>}
                {!loadingAccounts && pickerCandidates.length === 0 && (
                  <div className="text-zinc-500 text-sm py-4 text-center">
                    No unused accounts match &quot;{pickerSearch}&quot;.<br />
                    Every account is already used somewhere in this report.
                  </div>
                )}
                {pickerCandidates.map((a, i) => (
                  <button
                    key={a.id}
                    onClick={() => applyPickerPick(a)}
                    className="w-full text-left px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 hover:border-blue-500/40 hover:bg-blue-500/10 transition"
                    data-testid={`walkthrough-picker-candidate-${i}`}
                  >
                    <div className="text-sm font-semibold text-white truncate">{a.display_name || a.creditor_name}</div>
                    <div className="text-[11px] text-zinc-400 flex flex-wrap gap-x-3">
                      {a.account_number && <span>#{a.account_number}</span>}
                      {a.date_opened && <span>Opened {a.date_opened}</span>}
                      {a.balance ? <span className="text-emerald-300">${Number(a.balance).toLocaleString()}</span> : null}
                      {a.category && <span className="text-zinc-500">{a.category}</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Nested: "Copy old values?" prompt (per user's 4c) ────── */}
        {pendingCopy && (
          <div className="fixed inset-0 z-[130] bg-black/85 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-zinc-950 border border-amber-500/40 rounded-xl p-5">
              <h3 className="text-white font-bold text-lg mb-1">Copy old values?</h3>
              <p className="text-[12px] text-zinc-400 mb-3">
                You&apos;re replacing{' '}
                <span className="text-white font-semibold">{pendingCopy.oldAccount?.display_name || pendingCopy.oldAccount?.creditor_name}</span>{' '}
                with{' '}
                <span className="text-white font-semibold">{pendingCopy.newAccountRow?.creditor_name}</span>.
                {(pendingCopy.oldAccount?.balance || pendingCopy.oldAccount?.date_opened) && (
                  <> Do you want the new account to inherit the fraud amount and dates from the account you&apos;re removing?</>
                )}
              </p>
              <div className="text-[11px] text-zinc-500 bg-zinc-900 border border-zinc-800 rounded p-2 mb-3 space-y-0.5">
                <div>Old balance: <span className="text-emerald-300">${Number(pendingCopy.oldAccount?.balance || 0).toLocaleString()}</span></div>
                <div>Old date opened: <span className="text-zinc-300">{pendingCopy.oldAccount?.date_opened || '—'}</span></div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => finishReplace(true)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="walkthrough-copy-yes"
                >
                  Yes, copy values
                </Button>
                <Button
                  onClick={() => finishReplace(false)}
                  variant="outline"
                  className="flex-1 border-zinc-700 text-white"
                  data-testid="walkthrough-copy-no"
                >
                  No, use fresh values
                </Button>
              </div>
              <button
                onClick={() => setPendingCopy(null)}
                className="mt-2 w-full text-[11px] text-zinc-500 hover:text-white"
                data-testid="walkthrough-copy-cancel"
              >
                Cancel replacement
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
